import React from "react";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { ThemeProvider } from "@/contexts/ThemeContext";
import { PaywallProvider } from "@/contexts/PaywallContext";
import { NotificationsProvider } from "@/contexts/NotificationsContext";
import { Toaster } from "@/components/ui/sonner";
import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import AuthCallback from "@/pages/AuthCallback";
import ProfileSetup from "@/pages/ProfileSetup";
import VibeQuiz from "@/pages/VibeQuiz";
import AppShell from "@/pages/AppShell";
import MatchTab from "@/pages/tabs/Match";
import FindFlatsTab from "@/pages/tabs/FindFlats";
import FindRoommatesTab from "@/pages/tabs/FindRoommates";
import MenuTab from "@/pages/tabs/Menu";
import ChatTab from "@/pages/tabs/Chat";
import ChatList from "@/pages/ChatList";
import ChatRoom from "@/pages/ChatRoom";
import { PaymentSuccess, PaymentCancel } from "@/pages/PaymentPages";
import AdminLogin from "@/pages/admin/AdminLogin";
import AdminShell from "@/pages/admin/AdminShell";
import AdminOverview from "@/pages/admin/AdminOverview";
import AdminUsers from "@/pages/admin/AdminUsers";
import AdminVerifications from "@/pages/admin/AdminVerifications";
import AdminReports from "@/pages/admin/AdminReports";
import AdminBroadcast from "@/pages/admin/AdminBroadcast";
import AdminSettings from "@/pages/admin/AdminSettings";
import AdminLeads from "@/pages/admin/AdminLeads";
import AdminModeration from "@/pages/admin/AdminModeration";
import AdminListings from "@/pages/admin/AdminListings";
import AdminSubscriptions from "@/pages/admin/AdminSubscriptions";
import AdminActivity from "@/pages/admin/AdminActivity";
import "@/App.css";

function Protected({ children }) {
    const { status, user } = useAuth();
    if (status === "checking") {
        return (
            <div className="min-h-screen flex items-center justify-center bg-background">
                <div className="animate-pulse text-muted-foreground" data-testid="loading-spinner">Loading Flatch…</div>
            </div>
        );
    }
    if (status === "anon" || !user) return <Navigate to="/login" replace />;
    return children;
}

function Gate({ children }) {
    // Auth callback handler must run FIRST via hash detection
    const location = useLocation();
    if (location.hash?.includes("session_id=")) {
        return <AuthCallback />;
    }
    return children;
}

function Onboarded({ children }) {
    const { user } = useAuth();
    if (!user) return null;
    if (!user.profile_complete) return <Navigate to="/onboarding/profile" replace />;
    if (!user.vibe_complete) return <Navigate to="/onboarding/vibe" replace />;
    return children;
}

export default function App() {
    return (
        <div className="App">
            <ThemeProvider>
                <BrowserRouter>
                    <AuthProvider>
                        <PaywallProvider>
                            <NotificationsProvider>
                                <Gate>
                                <Routes>
                                    <Route path="/" element={<Landing />} />
                                    <Route path="/login" element={<Login />} />
                                    <Route path="/register" element={<Register />} />
                                    <Route path="/payment/success" element={<PaymentSuccess />} />
                                    <Route path="/payment/cancel" element={<PaymentCancel />} />

                                    {/* Private admin console — not linked from anywhere */}
                                    <Route path="/admin/login" element={<AdminLogin />} />
                                    <Route path="/admin" element={<AdminShell />}>
                                        <Route index element={<Navigate to="/admin/overview" replace />} />
                                        <Route path="overview" element={<AdminOverview />} />
                                        <Route path="users" element={<AdminUsers />} />
                                        <Route path="verifications" element={<AdminVerifications />} />
                                        <Route path="reports" element={<AdminReports />} />
                                        <Route path="broadcast" element={<AdminBroadcast />} />
                                        <Route path="settings" element={<AdminSettings />} />
                                        <Route path="leads" element={<AdminLeads />} />
                                        <Route path="moderation" element={<AdminModeration />} />
                                        <Route path="listings" element={<AdminListings />} />
                                        <Route path="subscriptions" element={<AdminSubscriptions />} />
                                        <Route path="activity" element={<AdminActivity />} />
                                    </Route>
                                    <Route
                                        path="/onboarding/profile"
                                        element={
                                            <Protected>
                                                <ProfileSetup />
                                            </Protected>
                                        }
                                    />
                                    <Route
                                        path="/onboarding/vibe"
                                        element={
                                            <Protected>
                                                <VibeQuiz />
                                            </Protected>
                                        }
                                    />
                                    <Route
                                        path="/app"
                                        element={
                                            <Protected>
                                                <Onboarded>
                                                    <AppShell />
                                                </Onboarded>
                                            </Protected>
                                        }
                                    >
                                        <Route index element={<Navigate to="/app/match" replace />} />
                                        <Route path="match" element={<MatchTab />} />
                                        <Route path="flats" element={<FindFlatsTab />} />
                                        <Route path="roommates" element={<FindRoommatesTab />} />
                                        <Route path="menu" element={<MenuTab />} />
                                        <Route path="chat" element={<ChatTab />} />
                                        <Route path="chats" element={<ChatList />} />
                                        <Route path="chats/:matchId" element={<ChatRoom />} />
                                    </Route>
                                    <Route path="*" element={<Navigate to="/" replace />} />
                                </Routes>
                            </Gate>
                            </NotificationsProvider>
                        </PaywallProvider>
                    </AuthProvider>
                </BrowserRouter>
            </ThemeProvider>
            <Toaster position="top-center" richColors />
        </div>
    );
}
